// Punktlista — SEO metadata for every route in this app.
//
// Two consumers read from here and they must not drift:
//   1. The React pages pass these values to RapportvaktShell (see
//      src/components/rapportvakt/Shell.jsx), which renders them via
//      react-helmet-async once the app has booted.
//   2. scripts/prerender-seo.mjs writes the same values into the static HTML
//      that Netlify serves, so a crawler that never runs JS sees them too.
//
// This is a trimmed copy of data-platform's src/config/seoRoutes.js, carrying
// over only the Rapportvakt/Punktlista section: this app has no other routes,
// no dataSources.js, and no hreflang pairs (the product is Swedish only).

// Explicit .js extension: scripts/prerender-seo.mjs loads this module in
// plain Node, which does not do Vite's extensionless resolution.
import {
  RAPPORTVAKT_APP_PATH,
  RAPPORTVAKT_DIGESTS,
  RAPPORTVAKT_HELP_PATH,
  RAPPORTVAKT_LOGIN_PATH,
  RAPPORTVAKT_PRICE,
  RAPPORTVAKT_PUBLISHERS,
  RAPPORTVAKT_PUBLISHERS_PATH,
  RAPPORTVAKT_PUBLISHER_COUNT,
  RAPPORTVAKT_SAMPLES,
  RAPPORTVAKT_SOURCE_COUNT,
  isoWeek,
  rapportvaktDigestPath,
  rapportvaktPublisherPath,
  rapportvaktSamplePath,
  seriesForPublisher,
} from './rapportvakt.js';

export const BASE_URL = 'https://punktlista.se';

const abs = (path) => `${BASE_URL}${path}`;

/**
 * Punktlista's own icons.
 *
 * Two consumers again: RapportvaktShell renders these through Helmet once the
 * app boots, and scripts/prerender-seo.mjs writes them into the static HTML.
 */
export const RAPPORTVAKT_ICONS = [
  { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
  { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
];

/**
 * The social card image.
 *
 * index.html declares twitter:card="summary_large_image", which promises an
 * image: without one, X shows no card at all and Slack/LinkedIn fall back to a
 * bare text link. One site-wide card rather than per-route images — the routes
 * differ in copy, not in artwork, and og:title/og:description already carry
 * the per-route part.
 *
 * Absolute URL on purpose: og:image is one of the few tags every scraper
 * requires to be absolute, so a relative path silently yields no image.
 */
export const RAPPORTVAKT_IMAGE = {
  url: `${BASE_URL}/og.png`,
  width: 1200,
  height: 630,
  alt: 'Punktlista: nya bostadsrapporter, sammanfattade till tre punkter.',
};

/**
 * The fields every Punktlista route shares.
 *
 * Swedish, no hreflang pair (this app has one language), its own icons and
 * its own og:site_name.
 */
const rapportvaktRoute = (route) => ({
  lang: 'sv',
  alternates: [],
  icons: RAPPORTVAKT_ICONS,
  image: RAPPORTVAKT_IMAGE,
  siteName: 'Punktlista',
  ...route,
});

export function getRapportvaktSeo() {
  return rapportvaktRoute({
    path: '/',
    title: 'Punktlista: tre punkter ur allt du följer',
    description:
      `Nya rapporter i tre punkter med sidnummer och pdf-länk, i den takt du väljer. ` +
      `${RAPPORTVAKT_SOURCE_COUNT} källor från ${RAPPORTVAKT_PUBLISHER_COUNT} avsändare, ${RAPPORTVAKT_PRICE.amount} per månad. Första källan gratis.`,
    canonical: abs('/'),
  });
}

/**
 * /utgivare — the publisher index.
 *
 * The explorative layer under the product: one page per avsändare, so a
 * search for "Boverket rapporter" has somewhere to land that is not the app.
 */
export function getRapportvaktPublishersSeo() {
  return rapportvaktRoute({
    path: RAPPORTVAKT_PUBLISHERS_PATH,
    title: 'Utgivare som följs | Punktlista',
    description:
      `De ${RAPPORTVAKT_PUBLISHER_COUNT} avsändarna inom bostad och samhällsbyggnad vars källor Punktlista följer, ` +
      `med ${RAPPORTVAKT_SOURCE_COUNT} källor och hur ofta var och en kommer.`,
    canonical: abs(RAPPORTVAKT_PUBLISHERS_PATH),
  });
}

export function getRapportvaktPublisherSeo(publisher) {
  const path = rapportvaktPublisherPath(publisher.slug);
  const series = seriesForPublisher(publisher);
  const count = series.length === 1 ? '1 källa' : `${series.length} källor`;
  return rapportvaktRoute({
    path,
    title: `${publisher.name}: källor | Punktlista`,
    // No report-name list here (it can run to hundreds of characters for a
    // publisher with several series) and no repeated publisher.name (it is
    // already the first word of publisher.description and of the title
    // above). Both were what pushed this past the 160-char budget.
    description: `${publisher.description} Punktlista följer ${count}.`,
    canonical: abs(path),
  });
}

/**
 * The product surface behind the landing page.
 *
 * It is prerendered like the rest so a crawler that lands on a shared app link
 * gets a real title, but its own copy points back at "/": the marketing page
 * is the one that should rank for the product.
 */
export function getRapportvaktAppSeo() {
  // Trailing slash: the build prerenders this as dist/app/index.html, which
  // Netlify serves at /app/ and 301s /app (no slash) to. RAPPORTVAKT_APP_PATH
  // itself stays without the slash because rapportvaktTabPath() builds
  // /app/<tab> from it and those tab URLs are client-side only (never
  // prerendered, never canonical) — see src/config/rapportvakt.js.
  const path = `${RAPPORTVAKT_APP_PATH}/`;
  return rapportvaktRoute({
    path,
    title: 'Dina utskick och källor | Punktlista',
    description:
      `Dina utskick i Punktlista: varje ny rapport i tre punkter med sidhänvisning, och de ${RAPPORTVAKT_SOURCE_COUNT} källor du kan välja att följa.`,
    canonical: abs(path),
  });
}

/**
 * /om — the about page (was /hjalp until the 2026-09 rename).
 *
 * Indexable on purpose: "hur funkar Punktlista", "sluta följa en källa" and
 * "saknas en källa" are questions a reader asks before they trust the
 * product, and the answers are the same ones a search engine should be able
 * to read. It is also the page every other surface now points a question at,
 * which makes it the product's only contact route besides the footer.
 */
export function getRapportvaktHelpSeo() {
  return rapportvaktRoute({
    path: RAPPORTVAKT_HELP_PATH,
    title: 'Om Punktlista | Punktlista',
    description:
      'Hur Punktlista fungerar, vad det kostar och hur du slutar följa en källa. ' +
      'Saknas en källa hör du av dig, så läggs den oftast till inom en dag.',
    canonical: abs(RAPPORTVAKT_HELP_PATH),
  });
}

export function getRapportvaktSampleSeo(sample) {
  const path = rapportvaktSamplePath(sample.slug);
  return rapportvaktRoute({
    path,
    title: `${sample.title}: tre punkter | Punktlista`,
    description: sample.metaDescription,
    canonical: abs(path),
  });
}

/**
 * One automated digest — today only the SCB weekly rollup at
 * /scb/vecka-<week>-<year>/, see rapportvaktDigestPath in rapportvakt.js.
 *
 * The description is the three points' own headlines joined into one
 * sentence rather than a hand-written summary, since nothing here should
 * characterise the week's content beyond what the points themselves already
 * say; metaDescription (written by scripts/export_digests.py from the
 * edition's own meta_description column) is the fallback for a digest whose
 * points carry no headline.
 *
 * jsonLd carries the Article structured data RapportvaktDigestPage injects
 * via Helmet (see RapportvaktShell's jsonLd prop) — not part of the head
 * tags scripts/prerender-seo.mjs writes, since that script only touches the
 * title/meta/canonical/OG tags every route shares; a crawler that reads
 * structured data reads it once react-helmet-async mounts, same as it
 * always has for this app's client-rendered content.
 */
export function getRapportvaktDigestSeo(digest) {
  const path = rapportvaktDigestPath(digest);
  const week = digest.windowStart ? isoWeek(digest.windowStart) : null;
  const title = week
    ? `SCB vecka ${week.week} ${week.year}: tre punkter | Punktlista`
    : `${digest.title}: tre punkter | Punktlista`;
  const headlines = (digest.points || []).map((point) => point.headline).filter(Boolean);
  const description = headlines.length ? `${headlines.join('. ')}.` : digest.metaDescription;
  const firstItemUrl = digest.items?.[0]?.url || null;

  return rapportvaktRoute({
    path,
    title,
    description,
    canonical: abs(path),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      datePublished: digest.dateIso,
      author: { '@type': 'Organization', name: 'Punktlista', url: BASE_URL },
      publisher: { '@type': 'Organization', name: 'Punktlista', url: BASE_URL },
      ...(firstItemUrl ? { isBasedOn: firstItemUrl } : {}),
      about: {
        '@type': 'Thing',
        creator: {
          '@type': 'GovernmentOrganization',
          name: digest.publisherName,
          url: 'https://www.scb.se/',
        },
      },
      // SCB web text is published under CC BY, credited "Källa: SCB" — see
      // the plan's "Decisions already made" and docs/research's source list.
      license: 'https://creativecommons.org/licenses/by/4.0/',
    },
  });
}

/**
 * /logga-in — where a mailed login link lands
 * (docs/plan/2026-09-11-magic-link-login.md).
 *
 * Not part of getPrerenderRoutes() below and never in the sitemap: this page
 * is only ever reached via a link from an email or a sign-in form elsewhere,
 * never something a search result should send a reader to (there is nothing
 * to see without a token in the query string, and a stale/reused link would
 * otherwise be exactly the kind of dead end an indexed page must not be) —
 * hence noindex, nofollow. It still needs a real file on disk for a hard
 * reload or a click straight from an email client to work, so
 * scripts/prerender-seo.mjs writes dist/logga-in/index.html directly,
 * mirroring how it writes dist/404.html below rather than looping this in
 * with the rest.
 */
export function getRapportvaktLoginSeo() {
  return rapportvaktRoute({
    path: RAPPORTVAKT_LOGIN_PATH,
    title: 'Logga in | Punktlista',
    description: 'Logga in på Punktlista med en länk som mejlas till dig. Inget lösenord.',
    canonical: abs(RAPPORTVAKT_LOGIN_PATH),
    robots: 'noindex, nofollow',
  });
}

/**
 * The 404 page.
 *
 * Not part of getPrerenderRoutes() below: it is not a real address (no
 * canonical, not in the sitemap), and scripts/prerender-seo.mjs writes it to
 * dist/404.html directly instead of looping it in with the rest. See
 * netlify.toml for how an unmatched request ends up there with a genuine 404
 * status rather than the 200 a SPA fallback would otherwise send.
 */
export function getRapportvaktNotFoundSeo() {
  return rapportvaktRoute({
    path: null,
    title: 'Sidan hittades inte | Punktlista',
    description: 'Sidan du sökte finns inte, eller så har den flyttat.',
    canonical: null,
    robots: 'noindex, nofollow',
  });
}

/**
 * Every route the build prerenders, in sitemap order.
 *
 * "/" IS included here (unlike data-platform's version of this file, where
 * the platform homepage is deliberately excluded because dist/index.html
 * doubles as the SPA fallback for many unrelated routes). This app has no
 * other routes for "/" to be mistaken for, so writing its own canonical is
 * safe and correct.
 */
export function getPrerenderRoutes() {
  return [
    getRapportvaktSeo(),
    getRapportvaktAppSeo(),
    getRapportvaktHelpSeo(),
    getRapportvaktPublishersSeo(),
    ...RAPPORTVAKT_PUBLISHERS.map((publisher) => getRapportvaktPublisherSeo(publisher)),
    ...RAPPORTVAKT_SAMPLES.map((sample) => getRapportvaktSampleSeo(sample)),
    ...RAPPORTVAKT_DIGESTS.map((digest) => getRapportvaktDigestSeo(digest)),
  ];
}
